import { useState } from 'react'
import { toast } from 'sonner'

import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useGetPdfUrlMutation } from '@/store/api/Person/Person.query'

interface UsePdfDownloadProps {
  reportId: string
}

interface UsePdfDownloadReturn {
  downloadPdf: () => Promise<void>
  isDownloading: boolean
}

/**
 * Hook to handle PDF downloads using CloudFront signed URLs
 * This hook fetches a temporary signed URL from the backend and opens it
 */
export const usePdfDownload = ({
  reportId,
}: UsePdfDownloadProps): UsePdfDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [getPdfUrl] = useGetPdfUrlMutation()

  const downloadPdf = async () => {
    if (!reportId) {
      toast.error('Report ID is missing')
      return
    }

    setIsDownloading(true)
    try {
      const response = await getPdfUrl({ id: reportId }).unwrap()

      if (response.info.url) {
        // Open the signed URL in a new tab
        // The URL already contains the signature parameters
        window.open(response.info.url, '_blank')
      } else {
        toast.error('PDF URL not available')
      }
    } catch (error) {
      catchWithSentry(error)
      console.error('Error downloading PDF:', error)
      toast.error('Failed to download PDF. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  return {
    downloadPdf,
    isDownloading,
  }
}
